// React
import React, { useEffect, useState } from "react";

// MUI
import { IconButton } from "@mui/material";

import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import { useLocation } from "react-router-dom";

// Components
import NotificationPopover from "components/NotificationPopover";

import SidebarLogo from "./_components/SidebarLogo";
import SidebarMenuList from "./_components/SidebarMenuList";
import SidebarToggleButton from "./_components/SidebarToggleButton";
// Styled Components
import { DrawerHeader, SidebarStyled, StyledDrawer } from "./_components/StyledComponents";
import UnifiedSearch from "./_components/UnifiedSearch";

/**
 * Main sidebar. The open/closed state lives in MainLayout (one source of truth):
 * - isMobile (< 768): temporary overlay drawer, always expanded inside, `open` shows/hides it
 * - from 768: permanent column, `open` = expanded (235) / collapsed (80)
 *
 * @param {boolean} props.open
 * @param {function} props.onToggle - called with the next boolean
 * @param {boolean} [props.isMobile]
 */
const MainSidebar = ({ open, onToggle, isMobile = false }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [notificationPopoverOpen, setNotificationPopoverOpen] = useState(false);

  // The overlay drawer closes when the user navigates
  useEffect(() => {
    if (isMobile) onToggle(false);
  }, [pathname, isMobile, onToggle]);

  // Inside the overlay the content is always the expanded one
  const expanded = isMobile || open;

  const handleNotificationClick = (e) => {
    e.preventDefault();
    setNotificationPopoverOpen(true);
  };

  return (
    <SidebarStyled open={open} mobile={isMobile}>
      <StyledDrawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        expanded={expanded}
        mobile={isMobile}
        onClose={() => onToggle(false)}
        ModalProps={{ keepMounted: true }}
        anchor="left"
      >
        <DrawerHeader>
          <SidebarLogo open={expanded} />
          {isMobile && (
            <IconButton
              size="small"
              onClick={() => onToggle(false)}
              aria-label={t("shell:sidebar.close")}
              sx={{ position: "absolute", top: 14, right: 8 }}
            >
              <FiX size={18} />
            </IconButton>
          )}
        </DrawerHeader>
        <UnifiedSearch open={expanded} onExpand={() => onToggle(true)} />
        <SidebarMenuList open={expanded} onNotificationClick={handleNotificationClick} />
      </StyledDrawer>

      {!isMobile && <SidebarToggleButton open={open} onToggle={() => onToggle(!open)} />}

      {/* Notification Popover */}
      <NotificationPopover
        open={notificationPopoverOpen}
        onClose={() => setNotificationPopoverOpen(false)}
        isSidebarCollapsed={!expanded}
      />
    </SidebarStyled>
  );
};

export default MainSidebar;
